import React, { useState } from 'react';
import { UserCog, UserX, XCircle } from 'lucide-react';
import Dialog from '../common/Dialog';
import { useToast } from '../common/Toast';
import CancelShiftDialog from './CancelShiftDialog';
import WarningList from './WarningList';
import { ClinicianPicker, PickerOption } from './Pickers';
import { ScheduleService, scheduleErrorMessage, warningsFromError } from '../../services/scheduling';
import type { UserData } from '../../dtos/admin/user.dto';
import type { ScheduleWarning, Shift } from '../../dtos/scheduling/schedule.dto';

export interface BulkBarProps {
  selected: Shift[];
  clinicians: UserData[];
  today: string;
  onClear: () => void;
  onDone: () => void;
}

/**
 * Actions on the selected shifts (plan §6.1): cancel with a reason, reassign, unassign.
 * Covers holds, discharge and clinician changes without editing shifts one by one.
 */
const BulkBar: React.FC<BulkBarProps> = ({ selected, clinicians, today, onClear, onDone }) => {
  const { showToast } = useToast();
  const [mode, setMode] = useState<'cancel' | 'reassign' | null>(null);
  const [busy, setBusy] = useState(false);
  const [provider, setProvider] = useState<PickerOption | null>(null);
  const [warnings, setWarnings] = useState<ScheduleWarning[] | null>(null);
  const [addToCareTeam, setAddToCareTeam] = useState(true);
  const ids = selected.map((s) => s.id);
  if (!selected.length) return null;

  const reassign = async (providerId: string | null, acknowledge = false) => {
    setBusy(true);
    try {
      const r = await ScheduleService.bulk('reassign', { ids, providerId, addToCareTeam, acknowledgeWarnings: acknowledge });
      showToast(`${providerId ? 'Reassigned' : 'Unassigned'} ${r.updated ?? 0} shift(s)${r.skipped?.length ? `; ${r.skipped.length} skipped (started or not editable)` : ''}`, { variant: 'success' });
      setMode(null);
      setWarnings(null);
      onDone();
    } catch (e) {
      const w = warningsFromError(e);
      if (w) setWarnings(w);
      else showToast(scheduleErrorMessage(e, 'Reassign failed'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="sticky bottom-0 z-20 -mx-4 flex flex-wrap items-center gap-2 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-2px_6px_rgba(0,0,0,0.05)] md:mx-0 md:rounded-md md:border">
        <span className="text-sm font-medium text-gray-800">{selected.length} selected</span>
        <button type="button" className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-gray-300 px-3 text-sm" onClick={() => setMode('reassign')}><UserCog size={16} />Reassign</button>
        <button type="button" className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-gray-300 px-3 text-sm" disabled={busy} onClick={() => reassign(null)}><UserX size={16} />Unassign</button>
        <button type="button" className="flex min-h-[44px] items-center gap-1.5 rounded-md border border-red-300 px-3 text-sm text-red-700" onClick={() => setMode('cancel')}><XCircle size={16} />Cancel…</button>
        <button type="button" className="ml-auto min-h-[44px] px-3 text-sm text-gray-600 hover:text-gray-900" onClick={onClear}>Clear</button>
      </div>

      <CancelShiftDialog
        open={mode === 'cancel'}
        summary={`${selected.length} shift${selected.length === 1 ? '' : 's'}`}
        busy={busy}
        onCancel={() => setMode(null)}
        onConfirm={async (code, note) => {
          setBusy(true);
          try {
            const r = await ScheduleService.bulk('cancel', { ids, code, note });
            showToast(`Cancelled ${r.cancelled ?? 0} shift(s)${r.skipped?.length ? `; ${r.skipped.length} skipped` : ''}`, { variant: 'success' });
            setMode(null);
            onDone();
          } catch (e) {
            showToast(scheduleErrorMessage(e, 'Cancel failed'), { variant: 'error' });
          } finally {
            setBusy(false);
          }
        }}
      />

      <Dialog open={mode === 'reassign'} onClose={() => { setMode(null); setWarnings(null); }} labelledBy="bulk-reassign-title">
        <h2 id="bulk-reassign-title" className="text-lg font-semibold text-gray-900">Reassign {selected.length} shift{selected.length === 1 ? '' : 's'}</h2>
        <div className="mt-4">
          <ClinicianPicker value={provider} clinicians={clinicians} today={today} allowUnstaffed={false} label="New clinician" onChange={(o) => { setProvider(o); setWarnings(null); }} />
        </div>
        {warnings && <div className="mt-4"><WarningList warnings={warnings} addToCareTeam={addToCareTeam} onAddToCareTeamChange={setAddToCareTeam} /></div>}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="min-h-[44px] rounded-md border border-gray-300 px-4 text-sm" onClick={() => { setMode(null); setWarnings(null); }}>Close</button>
          <button
            type="button"
            disabled={!provider || busy}
            className={`min-h-[44px] rounded-md px-4 text-sm font-semibold text-white disabled:opacity-50 ${warnings?.length ? 'bg-amber-600' : 'bg-slate-800'}`}
            onClick={() => provider && reassign(provider.id, Boolean(warnings?.length))}
          >
            {busy ? 'Saving…' : warnings?.length ? 'Reassign anyway' : 'Reassign'}
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default BulkBar;
